import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type ConnectorPosition = 'top' | 'bottom' | 'left' | 'right';
export type ArrowStyle = 'bezier' | 'straight' | 'step';
export type ConnectionType = 'default' | 'causes' | 'blocks' | 'contains' | 'sequence' | 'related';

export interface Arrow {
  id: string;
  board_id: string;
  user_id: string;
  start_item_id: string | null;
  start_connector: ConnectorPosition | null;
  start_x: number | null;
  start_y: number | null;
  end_item_id: string | null;
  end_connector: ConnectorPosition | null;
  end_x: number | null;
  end_y: number | null;
  start_arrowhead: boolean;
  end_arrowhead: boolean;
  stroke_color: string;
  created_at: string;
  updated_at: string;
  // Enhanced connection fields
  label?: string | null;
  style?: ArrowStyle;
  stroke_width?: number;
  animated?: boolean;
  connection_type?: ConnectionType;
  created_by?: 'user' | 'ai';
}

export interface ArrowDraft {
  startItemId: string | null;
  startConnector: ConnectorPosition | null;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  /** Started from a card's "Connect to..." menu: the next click picks the target card */
  fromMenu?: boolean;
}

export interface ArrowState {
  arrows: Arrow[];
  selectedArrowId: string | null;
  isArrowMode: boolean;
  isDrawing: boolean;
  arrowDraft: ArrowDraft | null;
  hoveredConnector: { itemId: string; position: ConnectorPosition } | null;
  
  // Actions
  setArrowMode: (mode: boolean) => void;
  setSelectedArrow: (arrowId: string | null) => void;
  setHoveredConnector: (connector: { itemId: string; position: ConnectorPosition } | null) => void;
  
  startDrawing: (
    startX: number,
    startY: number,
    startItemId?: string,
    startConnector?: ConnectorPosition,
    options?: { fromMenu?: boolean },
  ) => void;
  updateDrawing: (endX: number, endY: number) => void;
  finishDrawing: (boardId: string, endItemId?: string, endConnector?: ConnectorPosition) => Promise<void>;
  cancelDrawing: () => void;
  
  fetchArrows: (boardId: string) => Promise<void>;
  createArrow: (arrow: Partial<Arrow>) => Promise<Arrow | null>;
  createArrowsBatch: (arrows: Partial<Arrow>[]) => Promise<Arrow[]>;
  updateArrow: (id: string, updates: Partial<Arrow>) => Promise<void>;
  deleteArrow: (id: string) => Promise<void>;
  deleteArrowsByItem: (itemId: string) => Promise<void>;

  // Queries
  getArrowsForItem: (itemId: string) => Arrow[];
  getArrowBetween: (startItemId: string, endItemId: string) => Arrow | null;

  resetStore: () => void;
}

export const useArrowStore = create<ArrowState>((set, get) => ({
  arrows: [],
  selectedArrowId: null,
  isArrowMode: false,
  isDrawing: false,
  arrowDraft: null,
  hoveredConnector: null,
  
  setArrowMode: (mode) => {
    set({ isArrowMode: mode });
    if (!mode) {
      get().cancelDrawing();
      // Do NOT clear selectedArrowId here — let users keep their selection
      // after exiting arrow mode so the formatting sidebar stays visible
    }
  },
  
  setSelectedArrow: (arrowId) => set({ selectedArrowId: arrowId }),
  
  setHoveredConnector: (connector) => set({ hoveredConnector: connector }),
  
  startDrawing: (startX, startY, startItemId, startConnector, options) => {
    set({
      isDrawing: true,
      arrowDraft: {
        startItemId: startItemId || null,
        startConnector: startConnector || null,
        startX,
        startY,
        endX: startX,
        endY: startY,
        fromMenu: options?.fromMenu ?? false,
      },
    });
  },
  
  updateDrawing: (endX, endY) => {
    const { arrowDraft } = get();
    if (!arrowDraft) return;
    
    set({
      arrowDraft: { ...arrowDraft, endX, endY },
    });
  },
  
  finishDrawing: async (boardId, endItemId, endConnector) => {
    const { arrowDraft } = get();
    if (!arrowDraft) return;

    // Capture draft data and immediately clear drawing state to prevent
    // duplicate arrows from the dual mouseup handlers (div + window)
    const draft = { ...arrowDraft };
    set({ isDrawing: false, arrowDraft: null });

    // Don't create arrow if start and end are the same point (no drag)
    const dx = draft.endX - draft.startX;
    const dy = draft.endY - draft.startY;
    if (Math.sqrt(dx * dx + dy * dy) < 10) {
      return;
    }

    await get().createArrow({
      board_id: boardId,
      start_item_id: draft.startItemId,
      start_connector: draft.startConnector,
      start_x: draft.startItemId ? null : draft.startX,
      start_y: draft.startItemId ? null : draft.startY,
      end_item_id: endItemId || null,
      end_connector: endConnector || null,
      end_x: endItemId ? null : draft.endX,
      end_y: endItemId ? null : draft.endY,
      start_arrowhead: false,
      end_arrowhead: true,
      stroke_color: '#374151',
    });
  },
  
  cancelDrawing: () => {
    set({ isDrawing: false, arrowDraft: null });
  },
  
  fetchArrows: async (boardId) => {
    const { data, error } = await supabase
      .from('arrows')
      .select('*')
      .eq('board_id', boardId);
    
    if (error) {
      console.error('Error fetching arrows:', error);
      return;
    }
    
    set({ arrows: (data || []) as Arrow[] });
  },
  
  createArrow: async (arrowData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('You must be logged in');
      return null;
    }
    
    const insertData = {
      board_id: arrowData.board_id!,
      user_id: user.id,
      start_item_id: arrowData.start_item_id || null,
      start_connector: arrowData.start_connector || null,
      start_x: arrowData.start_x ?? null,
      start_y: arrowData.start_y ?? null,
      end_item_id: arrowData.end_item_id || null,
      end_connector: arrowData.end_connector || null,
      end_x: arrowData.end_x ?? null,
      end_y: arrowData.end_y ?? null,
      start_arrowhead: arrowData.start_arrowhead ?? false,
      end_arrowhead: arrowData.end_arrowhead ?? true,
      stroke_color: arrowData.stroke_color || '#374151',
      label: arrowData.label ?? null,
      style: arrowData.style ?? 'bezier',
      stroke_width: arrowData.stroke_width ?? 2,
      animated: arrowData.animated ?? false,
      connection_type: arrowData.connection_type ?? 'default',
      created_by: arrowData.created_by ?? 'user',
    };
    
    const { data, error } = await supabase
      .from('arrows')
      .insert(insertData)
      .select()
      .single();
    
    if (error) {
      console.error('Error creating arrow:', error);
      toast.error('Failed to create connector');
      return null;
    }
    
    const arrow = data as Arrow;
    set(state => ({ arrows: [...state.arrows, arrow] }));

    // Populate graph relationship for item-to-item arrows
    // Table not yet in generated Supabase types — use untyped call
    if (arrow.start_item_id && arrow.end_item_id) {
      (supabase as any).from('item_relationships').upsert({
        source_item_id: arrow.start_item_id,
        target_item_id: arrow.end_item_id,
        user_id: user.id,
        relationship_type: 'arrow',
        strength: 1.0,
      }, { onConflict: 'source_item_id,target_item_id,relationship_type' })
        .then(({ error: relError }: any) => {
          if (relError) console.warn('[Arrows] Failed to upsert relationship:', relError);
        });
    }

    return arrow;
  },
  
  updateArrow: async (id, updates) => {
    // Optimistic update
    set(state => ({
      arrows: state.arrows.map(a => a.id === id ? { ...a, ...updates } : a),
    }));
    
    const { error } = await supabase
      .from('arrows')
      .update(updates)
      .eq('id', id);
    
    if (error) {
      console.error('Error updating arrow:', error);
      toast.error('Failed to update connector');
      // Refetch to revert
      const state = get();
      if (state.arrows.length > 0) {
        get().fetchArrows(state.arrows[0].board_id);
      }
    }
  },
  
  deleteArrow: async (id) => {
    // Optimistic delete
    set(state => ({
      arrows: state.arrows.filter(a => a.id !== id),
      selectedArrowId: state.selectedArrowId === id ? null : state.selectedArrowId,
    }));
    
    const { error } = await supabase
      .from('arrows')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting arrow:', error);
      toast.error('Failed to delete connector');
    }
  },
  
  deleteArrowsByItem: async (itemId) => {
    // Get arrows connected to this item
    const { arrows } = get();
    const connectedArrows = arrows.filter(
      a => a.start_item_id === itemId || a.end_item_id === itemId
    );
    
    if (connectedArrows.length === 0) return;
    
    // Optimistic delete
    set(state => ({
      arrows: state.arrows.filter(a => 
        a.start_item_id !== itemId && a.end_item_id !== itemId
      ),
    }));
    
    // Delete from database
    const { error } = await supabase
      .from('arrows')
      .delete()
      .or(`start_item_id.eq.${itemId},end_item_id.eq.${itemId}`);
    
    if (error) {
      console.error('Error deleting arrows by item:', error);
    }
  },
  
  createArrowsBatch: async (arrowsData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const insertRows = arrowsData.map(a => ({
      board_id: a.board_id!,
      user_id: user.id,
      start_item_id: a.start_item_id || null,
      start_connector: a.start_connector || null,
      start_x: a.start_x ?? null,
      start_y: a.start_y ?? null,
      end_item_id: a.end_item_id || null,
      end_connector: a.end_connector || null,
      end_x: a.end_x ?? null,
      end_y: a.end_y ?? null,
      start_arrowhead: a.start_arrowhead ?? false,
      end_arrowhead: a.end_arrowhead ?? true,
      stroke_color: a.stroke_color || '#374151',
      label: a.label ?? null,
      style: a.style ?? 'bezier',
      stroke_width: a.stroke_width ?? 2,
      animated: a.animated ?? false,
      connection_type: a.connection_type ?? 'default',
      created_by: a.created_by ?? 'user',
    }));

    const { data, error } = await supabase
      .from('arrows')
      .insert(insertRows)
      .select();

    if (error) {
      console.error('Error batch creating arrows:', error);
      return [];
    }

    const created = (data || []) as Arrow[];
    set(state => ({ arrows: [...state.arrows, ...created] }));

    // Populate graph relationships for item-to-item arrows
    // Table not yet in generated Supabase types — use untyped call
    for (const arrow of created) {
      if (arrow.start_item_id && arrow.end_item_id) {
        (supabase as any).from('item_relationships').upsert({
          source_item_id: arrow.start_item_id,
          target_item_id: arrow.end_item_id,
          user_id: user.id,
          relationship_type: 'arrow',
          strength: 1.0,
        }, { onConflict: 'source_item_id,target_item_id,relationship_type' })
          .then(({ error: relError }: any) => {
            if (relError) console.warn('[Arrows] Failed to upsert relationship:', relError);
          });
      }
    }

    return created;
  },

  getArrowsForItem: (itemId) => {
    return get().arrows.filter(
      a => a.start_item_id === itemId || a.end_item_id === itemId
    );
  },

  getArrowBetween: (startItemId, endItemId) => {
    return get().arrows.find(
      a => a.start_item_id === startItemId && a.end_item_id === endItemId
    ) || null;
  },

  resetStore: () => {
    set({
      arrows: [],
      selectedArrowId: null,
      isArrowMode: false,
      isDrawing: false,
      arrowDraft: null,
      hoveredConnector: null,
    });
  },
}));
